import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown } from '@dexma/ui-components';
import gtm from 'adapter/in/reports/config/gtm';
import i18n from 'asset/translation';
import useTextBlock from 'adapter/in/common/widget/TextBlock/useTextBlock';
import useBusinessInsight from 'adapter/in/common/widget/BusinessInsight/useBusinessInsight';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { CreateTextBlockCriteriaBuilder } from 'use-case/widgets/create/createTextBlockCriteria';
import { CreateBusinessInsightCriteriaBuilder } from 'use-case/widgets/create/createBusinessInsightCriteria';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { dispatchTextBlockData } from 'adapter/in/reports/contexts/TextBlockContext';
import { isSome } from 'fp-ts/lib/Option';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';
import styled, { css } from 'styled-components';
import { ReportAnalysisCoordinates } from 'adapter/in/common/analysis/reportAnalysisCoordinates';
import { useNewAnalysisFromTemplateOverlayModal } from 'adapter/in/reports/components/AddWidgetDropdown/useNewAnalysisFromTemplateOverlayModal';
import { SaveReportTemplateLayoutUseCase } from 'adapter/in/reports/hooks/useSaveReportTemplateLayout';
import { useFeatures } from 'adapter/in/common/context/Features/data';
import { BusinessInsight } from 'domain/entity/feature';

const ADD_WIDGET_MENU_ITEM = {
    ANALYSIS: 0,
    RICH_TEXT: 1,
    BUSINESS_INSIGHT: 2,
} as const;

type StaticAddWidgetDropdownItem = {
    id: typeof ADD_WIDGET_MENU_ITEM.ANALYSIS | typeof ADD_WIDGET_MENU_ITEM.RICH_TEXT;
    text: string;
    dataId: string;
};

type BusinessInsightAddWidgetDropdownItem = {
    id: typeof ADD_WIDGET_MENU_ITEM.BUSINESS_INSIGHT;
    text: string;
    dataId: string;
};

type AddWidgetDropdownItem = StaticAddWidgetDropdownItem | BusinessInsightAddWidgetDropdownItem;

const ADD_WIDGET_DROPDOWN_CONTENT: StaticAddWidgetDropdownItem[] = [
    {
        id: ADD_WIDGET_MENU_ITEM.ANALYSIS,
        text: i18n.t('advancedAnalytics.analysis.title'),
        dataId: gtm.addWidget,
    },
    {
        id: ADD_WIDGET_MENU_ITEM.RICH_TEXT,
        text: i18n.t('widgets.widgetMenu.text'),
        dataId: gtm.addTextWidget,
    },
];

type AddWidgetDropdownProps = {
    reportTemplateId: string;
    isLayoutOverlay?: boolean;
    contextPeriod?: string;
    onTextWidgetSelected?: () => void;
    onBusinessInsightWidgetSelected?: (businessInsight: BusinessInsight, businessInsightId: string) => void;
    analysisCoordinates?: ReportAnalysisCoordinates;
    updateTemplateLayout?: SaveReportTemplateLayoutUseCase;
};

export const AddWidgetDropdown = ({
    reportTemplateId,
    isLayoutOverlay,
    contextPeriod,
    onTextWidgetSelected,
    onBusinessInsightWidgetSelected,
    analysisCoordinates,
    updateTemplateLayout,
}: AddWidgetDropdownProps) => {
    const { createTextBlock } = useTextBlock();
    const { createBusinessInsight } = useBusinessInsight();
    const {
        execute: create,
        isLoading,
        data,
        error: createError,
    } = useCase(createTextBlock, false);
    const {
        execute: createBusinessInsightWidget,
        isLoading: isBusinessInsightLoading,
        data: businessInsightData,
        error: businessInsightCreateError,
    } = useCase(createBusinessInsight, false);
    const { readResult: features } = useFeatures();
    const { getLayouts } = dispatchReportLayoutsData();
    const [isCreating, setIsCreating] = useState(false);
    const [isCreatingBusinessInsight, setIsCreatingBusinessInsight] = useState(false);
    const { setEditorBeingEdited } = dispatchTextBlockData();
    const [pendingOverlayBusinessInsight, setPendingOverlayBusinessInsight] = useState<BusinessInsight | undefined>();
    const { handleOnConfirmModal, onConfirmModal } = useNewAnalysisFromTemplateOverlayModal(
        reportTemplateId,
        !!isLayoutOverlay,
        contextPeriod,
        analysisCoordinates,
        updateTemplateLayout
    );

    useUseCaseAlerts(data, createError, {
        successMessage: 'reports.reportTemplate.textBlockCreateSuccess',
        errorMessage: 'reports.reportTemplate.textBlockCreateError',
    });

    useUseCaseAlerts(businessInsightData, businessInsightCreateError, {
        successMessage: 'reports.reportTemplate.businessInsightCreateSuccess',
        errorMessage: 'reports.reportTemplate.businessInsightCreateError',
    });

    const { dropdownContent, businessInsightsByDataId } = useMemo<{
        dropdownContent: AddWidgetDropdownItem[];
        businessInsightsByDataId: Map<string, BusinessInsight>;
    }>(() => {
        const nextBusinessInsightsByDataId = new Map<string, BusinessInsight>();
        const businessInsightItems =
            isSome(features)
                ? features.value.flatMap((feature) =>
                      feature.businessInsights.map((businessInsight) => {
                          const dataId = `${gtm.addWidget}.${businessInsight.type}`;
                          nextBusinessInsightsByDataId.set(dataId, businessInsight);

                          return {
                              id: ADD_WIDGET_MENU_ITEM.BUSINESS_INSIGHT,
                              text: businessInsight.label,
                              dataId,
                          };
                      })
                  )
                : [];

        return {
            dropdownContent: [...ADD_WIDGET_DROPDOWN_CONTENT, ...businessInsightItems],
            businessInsightsByDataId: nextBusinessInsightsByDataId,
        };
    }, [features]);

    useEffect(() => {
        if (!isLoading && isCreating && reportTemplateId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(reportTemplateId).build());
        }
    }, [isLoading, isCreating, reportTemplateId]);

    useEffect(() => {
        if (!isBusinessInsightLoading && isCreatingBusinessInsight && reportTemplateId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(reportTemplateId).build());
        }
    }, [isBusinessInsightLoading, isCreatingBusinessInsight, reportTemplateId]);

    useEffect(() => {
        if (isSome(data)) {
            setEditorBeingEdited(data.value.id);
            setIsCreating(false);
        }
        if (isSome(createError)) {
            setIsCreating(false);
        }
    }, [data, createError]);

    useEffect(() => {
        if (isSome(businessInsightData) || isSome(businessInsightCreateError)) {
            setIsCreatingBusinessInsight(false);
        }
    }, [businessInsightData, businessInsightCreateError]);

    useEffect(() => {
        if (!pendingOverlayBusinessInsight || !isSome(businessInsightData)) return;

        onBusinessInsightWidgetSelected?.(pendingOverlayBusinessInsight, businessInsightData.value.id);
        setPendingOverlayBusinessInsight(undefined);
    }, [businessInsightData, onBusinessInsightWidgetSelected, pendingOverlayBusinessInsight]);

    useEffect(() => {
        if (pendingOverlayBusinessInsight && isSome(businessInsightCreateError)) {
            setPendingOverlayBusinessInsight(undefined);
        }
    }, [businessInsightCreateError, pendingOverlayBusinessInsight]);

    const handleItemSelected = (key: number) => {
        const selectedItem = dropdownContent[key];
        if (!selectedItem) return;

        switch (selectedItem.id) {
            case ADD_WIDGET_MENU_ITEM.ANALYSIS:
                handleOnConfirmModal();
                break;
            case ADD_WIDGET_MENU_ITEM.RICH_TEXT:
                if (isLayoutOverlay) {
                    onTextWidgetSelected?.();
                    break;
                }
                create(CreateTextBlockCriteriaBuilder.create().withDashboardId(reportTemplateId).withRichText('').build());
                setIsCreating(true);
                break;
            case ADD_WIDGET_MENU_ITEM.BUSINESS_INSIGHT: {
                const selectedBusinessInsight = businessInsightsByDataId.get(selectedItem.dataId);
                if (!selectedBusinessInsight) break;

                const createBusinessInsightCriteria = CreateBusinessInsightCriteriaBuilder.create()
                    .withDashboardId(reportTemplateId)
                    .withType(selectedBusinessInsight.type)
                    .build();

                if (isLayoutOverlay) {
                    setPendingOverlayBusinessInsight(selectedBusinessInsight);
                    createBusinessInsightWidget(createBusinessInsightCriteria);
                    break;
                }
                createBusinessInsightWidget(createBusinessInsightCriteria);
                setIsCreatingBusinessInsight(true);
                break;
            }
            default:
                break;
        }
    };
    return (
        <StyledAddWidgetDropdown data-testid={isLayoutOverlay ? 'add-widget-overlay' : 'add-widget'} $isLayoutOverlay={isLayoutOverlay}>
            <Dropdown
                content={dropdownContent}
                text={i18n.t('dashboards.edit.addWidget')}
                trigger={['click']}
                variant='primary'
                onItemSelected={handleItemSelected}
                dataId={gtm.addWidget}
            />
            {onConfirmModal}
        </StyledAddWidgetDropdown>
    );
};

const StyledAddWidgetDropdown = styled.div<{ $isLayoutOverlay?: boolean }>`
    display: inline-flex;

    ${({ $isLayoutOverlay }) =>
        $isLayoutOverlay &&
        css`
            .dropdown-button {
                height: 1.5rem;
                padding: 0 0.75rem;
                font-size: 0.75rem;
                line-height: 1.5rem;
                white-space: nowrap;
            }
        `}
`;
